import React from 'react';
import {
  DirectionsBoat as BoatIcon,
  Inventory as InventoryIcon,
  Inventory2 as ContainerIcon,
  Engineering as EngineeringIcon,
} from '@mui/icons-material';
import marineInspectionImg from '../assets/images/MarineInspection.jpeg';
import generalCargoImg from '../assets/images/GeneralAndLiquidCargo.avif';
import containerInspectionImg from '../assets/images/ContainerInspection.avif';
import projectCargoImg from '../assets/images/ProjectOverDimentional.jpeg';

// Central source of truth for all services. Used by the Home page,
// the Services listing, and each individual service detail page.
export const services = [
  {
    slug: 'marine-draft-surveys',
    title: 'Marine & Draft Surveys',
    icon: <BoatIcon sx={{ fontSize: 40, color: 'primary.main' }} />,
    image: marineInspectionImg,
    shortDescription:
      'Independent marine surveys at berth and anchorage, supporting safe and well-documented vessel operations.',
    intro:
      'Accurate, independent marine surveys carried out to recognised standards — protecting the interests of owners, charterers, traders and insurers.',
    overview:
      "Our marine surveyors attend vessels at India's major ports and anchorages to verify cargo quantity, condition and compliance. Whether you need a draft survey to settle a bulk cargo quantity, an on/off-hire survey to record a vessel's condition at the change of charter, or supervision during loading and discharge, our team delivers timely, evidence-based reporting you can rely on.",
    included: [
      'Draft surveys for accurate bulk cargo quantity determination',
      'On-hire and off-hire condition surveys',
      'Bunker surveys (Remaining On Board)',
      'Hatch and hold cleanliness inspection',
      'Loading and discharge supervision',
      'Pre-loading and pre-shipment condition checks',
    ],
    deliverables: [
      'Detailed survey report with calculations',
      'Photographic evidence of cargo and vessel condition',
      'Statement of facts and time logs',
      'Recommendations where applicable',
    ],
    applications: ['Bulk carriers', 'Tankers', 'General cargo vessels', 'Charter parties', 'P&I and H&M claims'],
    relatedSlugs: ['general-liquid-cargo', 'container-inspections', 'project-cargo'],
  },
  {
    slug: 'general-liquid-cargo',
    title: 'General & Liquid Cargo',
    icon: <InventoryIcon sx={{ fontSize: 40, color: 'primary.main' }} />,
    image: generalCargoImg,
    shortDescription:
      'Quality and quantity verification for dry bulk, break-bulk, liquid and chemical consignments.',
    intro:
      'Quality and quantity assurance for dry, break-bulk and liquid cargo — including chemical field inspections, loading supervision and independent sampling.',
    overview:
      'We verify that what is shipped matches what was agreed. Our surveyors supervise loading and transfer operations, draw representative samples, measure quantities and document condition at each stage — including dedicated field inspection for chemical and sensitive consignments. The result is an independent record that protects buyers, sellers and carriers and helps resolve discrepancies before they become disputes.',
    included: [
      'Quality and quantity (Q&Q) assessment',
      'Chemical field inspection & sampling',
      'Loading, lashing and securing supervision',
      'Independent sampling and tally services',
      'Ullage, sounding and shore-tank measurement',
      'Weighbridge and draft-based quantity checks',
    ],
    deliverables: [
      'Quality and quantity certificate',
      'Sampling and tally records',
      'Loading / discharge supervision report',
      'Photographic documentation',
    ],
    applications: ['Agricultural commodities', 'Minerals & ores', 'Chemicals & petrochemicals', 'Steel & metals', 'Edible oils'],
    relatedSlugs: ['marine-draft-surveys', 'container-inspections', 'project-cargo'],
  },
  {
    slug: 'container-inspections',
    title: 'Container Inspections',
    icon: <ContainerIcon sx={{ fontSize: 40, color: 'primary.main' }} />,
    image: containerInspectionImg,
    shortDescription:
      'Container condition and stuffing inspections that protect cargo integrity and support secure shipment.',
    intro:
      'Container condition, stuffing and seal inspections that keep cargo secure and shipments compliant from origin to destination.',
    overview:
      'Damaged or poorly stuffed containers are a common and avoidable source of cargo loss. Our inspectors assess container suitability before stuffing, supervise the loading and securing of cargo, verify seals and document the entire process. We handle standard, reefer, tank and special-purpose containers for both shippers and equipment owners.',
    included: [
      'Pre-stuffing / pre-shipment container inspection',
      'On-hire and off-hire container surveys',
      'Cargo stuffing and securing supervision',
      'Seal verification and recording',
      'Reefer temperature and functionality checks',
      'Tank and special container suitability checks',
    ],
    deliverables: [
      'Container inspection report with checklist',
      'Photographic record of condition and stuffing',
      'Seal verification record',
      'Repair estimate where required',
    ],
    applications: ['FCL / LCL shipments', 'Reefer cargo', 'Tank containers', 'Container leasing', 'Export documentation'],
    relatedSlugs: ['marine-draft-surveys', 'general-liquid-cargo', 'project-cargo'],
  },
  {
    slug: 'project-cargo',
    title: 'Project & Over-Dimensional Cargo',
    icon: <EngineeringIcon sx={{ fontSize: 40, color: 'primary.main' }} />,
    image: projectCargoImg,
    shortDescription:
      'Specialised supervision for heavy-lift and oversized project consignments, from origin to final placement.',
    intro:
      'Specialised supervision for heavy-lift and over-dimensional cargo — ensuring safe handling, transport and placement at every stage.',
    overview:
      'Project and over-dimensional cargo carries high value and high risk. Our surveyors verify dimensions and weights, supervise lifting and lashing, and monitor transport and placement to ensure the consignment arrives intact. We provide continuous, photo-supported documentation that gives all stakeholders confidence throughout the move.',
    included: [
      'Dimensional and weight verification',
      'Lifting, lashing and securing supervision',
      'Loading and unloading inspection',
      'Route and transport monitoring',
      'Pre- and post-movement condition surveys',
      'Photographic and video documentation',
    ],
    deliverables: [
      'Project cargo supervision report',
      'Dimensional and weight records',
      'Lashing and securing certificate',
      'Comprehensive photo / video evidence',
    ],
    applications: ['Heavy machinery', 'Wind energy components', 'Industrial plant', 'Infrastructure projects', 'Oversized equipment'],
    relatedSlugs: ['marine-draft-surveys', 'general-liquid-cargo', 'container-inspections'],
  },
];

export const getServiceBySlug = (slug) => services.find((s) => s.slug === slug);

export const getRelatedServices = (slug) => {
  const service = getServiceBySlug(slug);
  if (!service) return [];
  return service.relatedSlugs.map(getServiceBySlug).filter(Boolean);
};
